import React, { useEffect, useState } from 'react';
import {
    Typography,
    Chip,
    Box,
    Paper,
    Container,
    TextField,
    CircularProgress,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import styled from 'styled-components';
import api from '../../../services/api';
import NavBar from 'src/components/organisms/navbar';
import { toast } from "react-toastify";
import useAuthenticate from 'src/hooks/useAuthenticate';
import Loading from 'src/components/atoms/loading';

interface Tournament {
    id: number;
    name: string;
    created_at: Date;
    updated_at: Date;
    descriptions: string;
    status: boolean;
    slug: string;
    authorId: number;
    date: string;
    location: string;
    canEditSubscriptions: boolean;
    canSubscribe: boolean;
    supportersId: number;
    paymentCredicard: boolean;
    paymentManual: boolean;
    paymentPix: boolean;
    bankAccountId: number;
    priceCombo: number;
    rules: JSON;
    isFederated: boolean;
    federationSlug: string | null;
    requireAffiliation: boolean;
}

interface FederationConfig {
    id: string | number;
    slug: string;
    name: string;
}

const PageContainer = styled.div`
  padding: 2rem;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;
`;

const ListItem = styled(Paper)`
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const ItemHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
`;

const TournamentsManagement: React.FC = () => {
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string | null>(null);
    const { getToken } = useAuthenticate();

    // Federation dialog state
    const [federationDialogOpen, setFederationDialogOpen] = useState(false);
    const [federationConfigs, setFederationConfigs] = useState<FederationConfig[]>([]);
    const [selectedFederationSlug, setSelectedFederationSlug] = useState<string>("");
    const [federationDialogTournament, setFederationDialogTournament] = useState<Tournament | null>(null);
    const [loadingFederations, setLoadingFederations] = useState(false);

    const fetchTournaments = async () => {
        try {
            const response = await api.get('/managers/tournaments/info', {
                headers: { Authorization: `Bearer ${getToken()}` },
            });

            const tournamentsSorted = response.data.sort((a: Tournament, b: Tournament) =>
                a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
            );

            setTournaments(tournamentsSorted);
        } catch (err) {
            console.error('Erro ao buscar torneios:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchFederationConfigs = async () => {
        setLoadingFederations(true);
        try {
            const response = await api.get('/managers/federation-configs', {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setFederationConfigs(response.data);
        } catch (err) {
            console.error('Erro ao buscar federacoes:', err);
            toast.error('Erro ao carregar federacoes disponiveis.');
        } finally {
            setLoadingFederations(false);
        }
    };

    const handleUpdateTournaments = async (tournamentId: number, operation: string, tournamentStatus: boolean, extra?: { federationSlug?: string }) => {
        try {
            const data: any = {
                operation: operation,
                currentStatus: tournamentStatus,
            };

            if (extra?.federationSlug) {
                data.federationSlug = extra.federationSlug;
            }

            await api.put(`/managers/tournaments/${tournamentId}`, data, {
                headers: {
                    Authorization: `Bearer ${getToken()}`,
                }
            });

            toast.success("Torneio atualizado com sucesso!");

            if (operation === 'isFederated') {
                const newIsFederated = !tournamentStatus;
                setTournaments((prev) =>
                    prev.map((t) =>
                        t.id === tournamentId
                            ? {
                                ...t,
                                isFederated: newIsFederated,
                                federationSlug: newIsFederated ? (extra?.federationSlug || null) : null,
                                requireAffiliation: newIsFederated ? t.requireAffiliation : false,
                            }
                            : t
                    )
                );
            } else {
                setTournaments((prev) =>
                    prev.map((t) =>
                        t.id === tournamentId ? { ...t, [operation]: !tournamentStatus } : t
                    )
                );
            }
        } catch (error: any) {
            toast.error('Erro ao atualizar torneio. Por favor, tente novamente.');
            setLoading(false);
        }
    };

    const handleFederationChipClick = (tournament: Tournament) => {
        if (tournament.isFederated) {
            // Desvinculando: confirma e remove
            handleUpdateTournaments(tournament.id, "isFederated", tournament.isFederated);
        } else {
            // Vinculando: abre dialog para selecionar federacao
            setFederationDialogTournament(tournament);
            setSelectedFederationSlug("");
            setFederationDialogOpen(true);
            fetchFederationConfigs();
        }
    };

    const handleFederationDialogConfirm = () => {
        if (!federationDialogTournament || !selectedFederationSlug) return;

        handleUpdateTournaments(
            federationDialogTournament.id,
            "isFederated",
            federationDialogTournament.isFederated,
            { federationSlug: selectedFederationSlug }
        );

        setFederationDialogOpen(false);
        setFederationDialogTournament(null);
        setSelectedFederationSlug("");
    };

    const handleFederationDialogClose = () => {
        setFederationDialogOpen(false);
        setFederationDialogTournament(null);
        setSelectedFederationSlug("");
    };

    useEffect(() => {
        fetchTournaments();
    }, []);

    // filtros de pesquisa e select
    const handleFilter = (event: any) => {
        const value = event.target.value;
        setFilter(value === "" ? null : value);
    };

    const filteredData = filter
        ? tournaments.filter((item: any) => item.id === parseInt(filter))
        : tournaments;

    if (loading) {
        return (
            <Loading text='Aguarde, estamos carregando os dados dos torneios...' />
        );
    }

    const getColor = (value: boolean | null | undefined) => {
        if (value === true) return "success";
        if (value === false) return "error";
        return "warning";
    };

    return (
        <>
            <NavBar />
            <Container maxWidth="lg" sx={{ mb: 4 }}>
                <PageContainer>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'initial' }}>
                        <Typography variant="h4" gutterBottom>Gerenciar Torneios</Typography>

                        {filteredData.length === 1 ? (
                            <Typography variant="subtitle1" gutterBottom>{filteredData.length} Torneio Encontrado</Typography>
                        ) : (
                            (
                                <Typography variant="subtitle1" gutterBottom>{filteredData.length} Torneios Encontrados</Typography>
                            )
                        )}

                    </Box>
                    <ListContainer>
                        <Box sx={{ width: '100%' }}>
                            <Autocomplete
                                options={tournaments
                                    .filter(t => tournaments.some((d: any) => d.id === t.id))
                                    .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))}
                                getOptionLabel={(option) => option.name.toUpperCase()}
                                value={tournaments.find(t => t.id === parseInt(filter || "0")) || null}
                                onChange={(event, newValue) => {
                                    handleFilter({ target: { value: newValue ? newValue.id.toString() : "" } });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Digite para buscar um torneio..."
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "white"
                                            }
                                        }}
                                    />
                                )}

                                noOptionsText="Nenhum torneio encontrado"
                                loading={loading}
                                loadingText="Carregando torneios..."
                                sx={{
                                    "& .MuiAutocomplete-paper": {
                                        backgroundColor: "white"
                                    }
                                }}
                            />
                        </Box>
                        {filteredData.length > 0 ? (
                            filteredData.map((tournament) => {
                                const chipConfigs = [
                                    {
                                        key: "status",
                                        value: tournament.status,
                                        labels: ["Torneio Ativo", "Torneio Inativo", "Torneio Indisponivel"],
                                        size: "medium",
                                    },
                                    {
                                        key: "canSubscribe",
                                        value: tournament.canSubscribe,
                                        labels: ["Inscricoes Abertas", "Inscricoes Fechadas", "Inscricoes Indisponiveis"],
                                        size: "small",
                                    },
                                    {
                                        key: "canEditSubscriptions",
                                        value: tournament.canEditSubscriptions,
                                        labels: ["Correcoes Abertas", "Correcoes Fechadas", "Correcoes Indisponiveis"],
                                        size: "small",
                                    },
                                    {
                                        key: "paymentManual",
                                        value: tournament.paymentManual,
                                        labels: ["Pagamento Manual", "Pagamento Manual Inativo", "Pagamento Manual Indisponivel"],
                                        size: "small",
                                    },
                                    {
                                        key: "paymentPix",
                                        value: tournament.paymentPix,
                                        labels: ["Pagamento Pix", "Pagamento Pix Inativo", "Pagamento Pix Indisponivel"],
                                        size: "small",
                                    },
                                    {
                                        key: "paymentCredicard",
                                        value: tournament.paymentCredicard,
                                        labels: ["Pagamento Credito", "Pagamento Credito Inativo", "Pagamento Credito Indisponivel"],
                                        size: "small",
                                    },
                                    ...(tournament.isFederated ? [{
                                        key: "requireAffiliation",
                                        value: tournament.requireAffiliation,
                                        labels: ["Filiacao Obrigatoria", "Filiacao Opcional", "Filiacao Indisponivel"],
                                        size: "small",
                                    }] : []),
                                ];

                                const federationName = tournament.federationSlug
                                    ? federationConfigs.find(f => f.slug === tournament.federationSlug)?.name || tournament.federationSlug.toUpperCase()
                                    : null;

                                return (
                                    <ListItem key={tournament.id} sx={{ width: "96%" }}>
                                        <ItemHeader>
                                            <Typography variant="h6" fontFamily={"inter, sans-serif"}>
                                                {tournament.name.toLocaleUpperCase()}
                                            </Typography>

                                            {/* Chip de Status */}
                                            <Chip
                                                label={chipConfigs[0].labels[
                                                    tournament.status === true ? 0 : tournament.status === false ? 1 : 2
                                                ]}
                                                sx={{ "& .MuiChip-label": { fontWeight: "bold" } }}
                                                color={getColor(tournament.status)}
                                                size="medium"
                                                onClick={() =>
                                                    handleUpdateTournaments(tournament.id, "status", tournament.status)
                                                }
                                            />
                                        </ItemHeader>

                                        <Typography variant="body2" sx={{ mt: 1, fontFamily: "inter, sans-serif" }}>
                                            <strong>Slug:</strong> {tournament.slug || "N/A"}
                                        </Typography>

                                        <Typography variant="body2" sx={{ mt: 1, fontFamily: "inter, sans-serif" }}>
                                            <strong>Local do evento:</strong> {tournament.location || "N/A"}
                                        </Typography>

                                        <Typography variant="body2" sx={{ mt: 1, fontFamily: "inter, sans-serif" }}>
                                            <strong>Data do evento:</strong>{" "}
                                            {new Date(tournament.date + "T00:00:00").toLocaleDateString("pt-BR")}
                                        </Typography>

                                        <Typography variant="body2" sx={{ mt: 1, fontFamily: "inter, sans-serif" }}>
                                            <strong>Criado em:</strong>{" "}
                                            {new Date(tournament.created_at).toLocaleDateString("pt-BR")}
                                        </Typography>

                                        {tournament.isFederated && federationName && (
                                            <Typography variant="body2" sx={{ mt: 1, fontFamily: "inter, sans-serif" }}>
                                                <strong>Federacao:</strong> {federationName}
                                            </Typography>
                                        )}

                                        <ItemHeader>
                                            <Box
                                                maxWidth="lg"
                                                sx={{ display: "flex", flexDirection: "row", gap: 1, mt: 1, flexWrap: 'wrap' }}
                                            >
                                                {/* Chip de Federacao (tratamento especial) */}
                                                <Chip
                                                    label={tournament.isFederated
                                                        ? `Federado${federationName ? ` - ${federationName}` : ''}`
                                                        : "Nao Federado"
                                                    }
                                                    sx={{ "& .MuiChip-label": { fontWeight: "bold" } }}
                                                    color={tournament.isFederated ? "success" : "error"}
                                                    size="small"
                                                    onClick={() => handleFederationChipClick(tournament)}
                                                />

                                                {/* Demais chips (exceto isFederated que ja foi renderizado acima) */}
                                                {chipConfigs.slice(1).map((chip) => (
                                                    <Chip
                                                        key={chip.key}
                                                        label={
                                                            chip.labels[
                                                            chip.value === true ? 0 : chip.value === false ? 1 : 2
                                                            ]
                                                        }
                                                        sx={{ "& .MuiChip-label": { fontWeight: "bold" } }}
                                                        color={getColor(chip.value)}
                                                        size={chip.size as "small" | "medium"}
                                                        onClick={() =>
                                                            handleUpdateTournaments(tournament.id, chip.key, chip.value)
                                                        }
                                                    />
                                                ))}
                                            </Box>
                                        </ItemHeader>
                                    </ListItem>
                                );
                            })
                        ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                Nenhum torneio encontrado.
                            </Typography>
                        )}
                    </ListContainer>
                </PageContainer>
            </Container>

            {/* Dialog de selecao de federacao */}
            <Dialog
                open={federationDialogOpen}
                onClose={handleFederationDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontFamily: "inter, sans-serif" }}>
                    Vincular Federacao
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, fontFamily: "inter, sans-serif" }}>
                        Selecione a federacao para vincular ao torneio{" "}
                        <strong>{federationDialogTournament?.name}</strong>
                    </Typography>

                    {loadingFederations ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : federationConfigs.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "inter, sans-serif" }}>
                            Nenhuma federacao disponivel para integracao.
                        </Typography>
                    ) : (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id="federation-select-label">Federacao</InputLabel>
                            <Select
                                labelId="federation-select-label"
                                value={selectedFederationSlug}
                                label="Federacao"
                                onChange={(e) => setSelectedFederationSlug(e.target.value)}
                            >
                                {federationConfigs.map((federation) => (
                                    <MenuItem key={federation.slug} value={federation.slug}>
                                        {federation.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFederationDialogClose} sx={{ fontFamily: "inter, sans-serif" }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleFederationDialogConfirm}
                        variant="contained"
                        disabled={!selectedFederationSlug || loadingFederations}
                        sx={{ fontFamily: "inter, sans-serif" }}
                    >
                        Vincular
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TournamentsManagement;
